import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { PrintZone } from "@/components/print/DocumentPreview";
import { cn } from "@/lib/utils";
import { AreaPanel } from "./panels/AreaPanel";
import { AREAS } from "./printOptions";
import type { PrintScope } from "./printLayoutForm";
import type { ImageUploader } from "./useImageUpload";

type Props = {
  zone: PrintZone;
  onZoneChange: (zone: PrintZone) => void;
  /** Compact PreviewCard, built by the parent so it shares preview state. */
  preview: ReactNode;
  images: ImageUploader;
  scope: PrintScope;
  onPreviewDigital?: () => void;
  /** Cancel/Save buttons, rendered in the sticky bottom bar. */
  actions?: ReactNode;
};

export function MobilePrintLayoutEditor({ zone, onZoneChange, preview, images, scope, onPreviewDigital, actions }: Props) {
  const { t } = useTranslation("settings");
  return (
    <div className="-mx-5 flex flex-col gap-3">
      <div className="px-4 pt-2">{preview}</div>
      <div role="tablist" aria-label={t("printEditor.areasLabel")} className="flex gap-2 overflow-x-auto px-4 pb-2.5 pt-3">
        {AREAS.map(({ zone: z, key }) => {
          const active = z === zone;
          return (
            <button
              key={z}
              type="button"
              role="tab"
              id={`print-tab-${z}`}
              aria-selected={active}
              aria-controls="print-area-panel"
              onClick={() => onZoneChange(z)}
              className={cn(
                "relative h-10 shrink-0 rounded-full before:absolute before:-inset-y-0.5 before:inset-x-0 before:content-[''] px-3.5 text-[13.5px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                active ? "bg-foreground text-background" : "border bg-card",
              )}
            >
              {t(`printEditor.areas.${key}`)}
            </button>
          );
        })}
      </div>
      <section
        id="print-area-panel"
        role="tabpanel"
        aria-labelledby={`print-tab-${zone}`}
        className="mx-4 overflow-hidden rounded-card border border-border/60 bg-card text-card-foreground shadow-soft-card"
      >
        <AreaPanel zone={zone} images={images} onPreviewDigital={onPreviewDigital ?? (() => undefined)} scope={scope} variant="mobile" />
      </section>
      {actions ? (
        // Sticky (not fixed) so the on-screen keyboard never hides Save; offset clears the floating BottomNavigation.
        <div className="sticky bottom-[var(--bottom-nav-offset)] z-10 mt-2 flex flex-col-reverse gap-2 border-t bg-card px-4 pb-3 pt-3">
          {actions}
        </div>
      ) : null}
    </div>
  );
}
